import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiTrashLight,
  PiFileTextLight,
  PiSpinnerGapLight,
  PiUploadSimpleLight,
  PiCheckCircleLight,
  PiInfoLight,
  PiWarningLight,
  PiListLight,
  PiXLight,
  PiFileLight,
  PiScalesLight,
  PiUserLight,
  PiClockCounterClockwiseLight,
  PiNoteLight,
  PiQuestionLight,
  PiMagnifyingGlassLight,
  PiGavelLight,
  PiBriefcaseLight,
  PiListChecksLight,
  PiTextAlignLeftLight,
  PiLockKeyLight,
} from 'react-icons/pi';
import Tooltip from '@mui/material/Tooltip';
import apis from '../../../utils/apis';
import { hasCasesSupport } from '../../Matters/matterConstants';
import Button from '../../shared/TailwindComponents/Button';
import Textarea from '../../shared/TailwindComponents/Textarea';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import FileUploader from '../../shared/TailwindComponents/FileUploader';
import Badge from '../../shared/TailwindComponents/Badge';
import Toggle from '../../shared/TailwindComponents/Toggle';
import { useAppDispatch } from '../../../store';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { fetchStructureTemplates } from '../../../store/actions/TemplateActions';
import { useLicenseGate } from '../../../hooks/useLicenseGate';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, documentCustomizationTourSteps } from '../FeatureTour';
import Spinner from '../TailwindComponents/Spinner';
import { readErrorMessage } from '../../../utils/errorMessage';

interface ContextSectionConfig {
  key: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  defaultEnabled: boolean;
  alwaysOn?: boolean;
}

interface CustomPromptData {
  instructions: string;
  tone: string;
  focus_areas: string[];
  additional_context: string;
  structure_template_id: string;
  context_sections: Record<string, boolean>;
}

interface StructureTemplate {
  id: string;
  TemplateName: string;
  DraftType: string;
  status?: string;
  Structure?: {
    title?: string;
    description?: string;
    outline?: any[];
  };
}

interface CustomAttachment {
  filename: string;
  size_bytes: number;
  uploaded_at: string;
}

interface DocumentCustomizationPanelProps {
  matterUuid: string;
  documentTypeId: string;
  documentType: 'draft' | 'insight';
  documentName: string;
  onClose: () => void;
}

const getToneOptions = (t: (key: string) => string) => [
  { value: '', label: t('Default (Professional)') },
  { value: 'formal', label: t('Formal & Authoritative') },
  { value: 'persuasive', label: t('Persuasive & Compelling') },
  { value: 'neutral', label: t('Neutral & Objective') },
  { value: 'concise', label: t('Concise & Direct') },
  { value: 'detailed', label: t('Detailed & Comprehensive') },
  { value: 'empathetic', label: t('Empathetic & Client-Focused') },
];

const DEFAULT_CONTEXT_SECTIONS: Record<string, boolean> = {
  metadata: true,
  description: true,
  laws: true,
  cases: true,
  source_documents: true,
  questions: true,
  notes: true,
  tasks: true,
  client_info: true,
  custom_prompt: true,
  reference_documents: true,
  previous_version: true,
};

const CONTEXT_SECTION_CONFIGS: ContextSectionConfig[] = [
  {
    key: 'description',
    label: 'Matter Description',
    description: 'AI-generated matter summary and case description',
    icon: <PiTextAlignLeftLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
  {
    key: 'laws',
    label: 'Laws & Regulations',
    description: 'Relevant statutes and legal provisions from analysis',
    icon: <PiScalesLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
  {
    key: 'cases',
    label: 'Case Precedents',
    description: 'Relevant court decisions and legal precedents',
    icon: <PiGavelLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
  {
    key: 'source_documents',
    label: 'Source Documents',
    description: 'Uploaded case files and documents',
    icon: <PiFileLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
  {
    key: 'questions',
    label: 'Questionnaire Answers',
    description: 'Finalized answers from the matter questionnaire',
    icon: <PiQuestionLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
  {
    key: 'notes',
    label: 'Matter Notes',
    description: 'Notes and observations added to the matter',
    icon: <PiNoteLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
  {
    key: 'tasks',
    label: 'Matter Tasks',
    description: 'Suggested and active tasks related to the matter',
    icon: <PiListChecksLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
  {
    key: 'client_info',
    label: 'Client Information',
    description: 'Client details, roles, and contact information',
    icon: <PiUserLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
  {
    key: 'reference_documents',
    label: 'Reference Attachments',
    description: 'Custom reference files uploaded for this document',
    icon: <PiFileTextLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
  {
    key: 'previous_version',
    label: 'Previous Version',
    description: 'Content from the last generated version of this document',
    icon: <PiClockCounterClockwiseLight className="w-5 h-5" />,
    defaultEnabled: true,
  },
];

const EMPTY_PROMPT: CustomPromptData = {
  instructions: '',
  tone: '',
  focus_areas: [],
  additional_context: '',
  structure_template_id: '',
  context_sections: { ...DEFAULT_CONTEXT_SECTIONS },
};

const DocumentCustomizationPanel: React.FC<DocumentCustomizationPanelProps> = ({
  matterUuid,
  documentTypeId,
  documentType,
  documentName,
  onClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { hasSectionAccess, checkSectionGate } = useLicenseGate();
  // Structure templates are the Document Templates (`templates`) module. When
  // the org has not licensed it, the picker is replaced with an upgrade prompt
  // - draft/insight generation itself stays fully usable without a template.
  const templatesLicensed = hasSectionAccess('templates');

  // State
  const [prompt, setPrompt] = useState<CustomPromptData>({ ...EMPTY_PROMPT });
  const [originalPrompt, setOriginalPrompt] = useState<CustomPromptData>({ ...EMPTY_PROMPT });
  const [attachments, setAttachments] = useState<CustomAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingFile, setDeletingFile] = useState<string | null>(null);
  const [hasExistingPrompt, setHasExistingPrompt] = useState(false);
  const [focusAreaInput, setFocusAreaInput] = useState('');

  // Structure template state
  const [structureTemplates, setStructureTemplates] = useState<StructureTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [contextAvailability, setContextAvailability] = useState<Record<string, boolean>>({});
  const [matterJurisdiction, setMatterJurisdiction] = useState<string>('');
  const [hasPreviousVersion, setHasPreviousVersion] = useState(false);
  const basePath = `/${documentType}s/${matterUuid}/${documentTypeId}`;

  // Tour for the customization panel
  const {
    isRunning: isTourRunning,
    handleJoyrideCallback: handleTourCallback,
  } = useFeatureTour({
    tourId: 'document-customization-panel',
    steps: documentCustomizationTourSteps,
    readyToShow: !loading,
    autoStartDelay: 600,
  });

  // Load existing custom prompt and attachments on mount
  const fetchCustomization = useCallback(async () => {
    setLoading(true);
    try {
      const [promptRes, attachmentsRes] = await Promise.all([
        apis.get(`${basePath}/custom-prompt`).catch(() => null),
        apis.get(`${basePath}/attachments`).catch(() => null),
      ]);

      if (promptRes?.data?.prompt) {
        const p = promptRes.data.prompt;
        const loaded: CustomPromptData = {
          instructions: p.instructions || '',
          tone: p.tone || '',
          focus_areas: Array.isArray(p.focus_areas) ? p.focus_areas : [],
          additional_context: p.additional_context || '',
          structure_template_id: p.structure_template_id || '',
          context_sections: p.context_sections
            ? { ...DEFAULT_CONTEXT_SECTIONS, ...p.context_sections }
            : { ...DEFAULT_CONTEXT_SECTIONS },
        };
        setPrompt(loaded);
        setOriginalPrompt(loaded);
        setHasExistingPrompt(true);
      }

      if (attachmentsRes?.data?.attachments) {
        setAttachments(attachmentsRes.data.attachments);
      }
    } catch (error) {
      console.error('Failed to fetch customization:', error);
    } finally {
      setLoading(false);
    }
  }, [basePath]);

  // Fetch available structure templates
  const fetchTemplates = useCallback(async () => {
    // Don't reach for a module the org hasn't licensed - the picker is gated
    // below and the fetch would 403 anyway.
    if (!templatesLicensed) return;
    setLoadingTemplates(true);
    try {
      const result = await dispatch(fetchStructureTemplates({ page_size: 100 }) as any);
      if (result?.templates) {
        // Only show completed templates
        const completed = result.templates.filter(
          (t: StructureTemplate) => t.status === 'completed' || t.status === 'ready'
        );
        setStructureTemplates(completed);
      }
    } catch (error) {
      console.error('Failed to fetch structure templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  }, [dispatch, templatesLicensed]);

  // Fetch context data availability from backend
  const fetchContextAvailability = useCallback(async () => {
    try {
      // Fetch matter-level context availability
      const res = await apis.get(`/matters/${matterUuid}/context-availability`);
      if (res?.data?.availability) {
        setContextAvailability(res.data.availability);
      }
      if (res?.data?.jurisdiction) {
        setMatterJurisdiction(res.data.jurisdiction);
      }
    } catch (error) {
      console.error('Failed to fetch context availability:', error);
    }

    // Check if a previous version of this document exists
    try {
      const docRes = await apis.get(`${basePath}`).catch(() => null);
      if (docRes?.data?.content || docRes?.data?.document_metadata?.status === 'completed') {
        setHasPreviousVersion(true);
      }
    } catch {
      // No previous version exists
    }
  }, [matterUuid, basePath]);

  useEffect(() => {
    fetchCustomization();
    fetchTemplates();
    fetchContextAvailability();
  }, [fetchCustomization, fetchTemplates, fetchContextAvailability]);

  // Check if prompt has unsaved changes
  const hasChanges = JSON.stringify(prompt) !== JSON.stringify(originalPrompt);

  // Save custom prompt
  const handleSave = async () => {
    setSaving(true);
    try {
      const hasContextChanges = Object.entries(prompt.context_sections).some(
        ([key, val]) => val !== (DEFAULT_CONTEXT_SECTIONS[key] ?? true)
      );
      const hasContent = prompt.instructions || prompt.tone || prompt.focus_areas.length > 0 || prompt.additional_context || prompt.structure_template_id || hasContextChanges;

      if (!hasContent && hasExistingPrompt) {
        // If all fields are empty and there was an existing prompt, delete it
        await apis.delete(`${basePath}/custom-prompt`);
        setHasExistingPrompt(false);
        setOriginalPrompt({ ...EMPTY_PROMPT });
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Custom prompt removed'),
          modalType: 'success',
          duration: 3000,
        }));
      } else if (hasContent) {
        await apis.put(`${basePath}/custom-prompt`, {
          instructions: prompt.instructions,
          tone: prompt.tone,
          focus_areas: prompt.focus_areas,
          additional_context: prompt.additional_context,
          structure_template_id: prompt.structure_template_id,
          context_sections: prompt.context_sections,
        });
        setOriginalPrompt({ ...prompt });
        setHasExistingPrompt(true);
        dispatch(showFeedbackModal({
          showModal: true,
          message: t('Custom prompt saved successfully'),
          modalType: 'success',
          duration: 3000,
        }));
      }
    } catch (error) {
      console.error('Failed to save custom prompt:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to save custom prompt'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setSaving(false);
    }
  };

  // Upload attachments
  const handleUploadFiles = async (files: File[]) => {
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        await apis.post(`${basePath}/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      // Refresh the attachments list
      const res = await apis.get(`${basePath}/attachments`);
      if (res?.data?.attachments) {
        setAttachments(res.data.attachments);
      }
      dispatch(showFeedbackModal({
        showModal: true,
        message: t(files.length === 1 ? 'Attachment uploaded' : 'Attachments uploaded'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (error: any) {
      console.error('Failed to upload attachment:', error);
      const errMsg = readErrorMessage(error, t('Failed to upload attachment'));
      dispatch(showFeedbackModal({
        showModal: true,
        message: errMsg,
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setUploading(false);
    }
  };

  // Delete attachment
  const handleDeleteAttachment = async (filename: string) => {
    setDeletingFile(filename);
    try {
      await apis.delete(`${basePath}/attachments/${encodeURIComponent(filename)}`);
      setAttachments((prev) => prev.filter((a) => a.filename !== filename));
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Attachment removed'),
        modalType: 'success',
        duration: 3000,
      }));
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Failed to remove attachment'),
        modalType: 'error',
        duration: 3000,
      }));
    } finally {
      setDeletingFile(null);
    }
  };

  // Focus areas management
  const handleAddFocusArea = () => {
    const trimmed = focusAreaInput.trim();
    if (trimmed && !prompt.focus_areas.includes(trimmed)) {
      setPrompt((prev) => ({
        ...prev,
        focus_areas: [...prev.focus_areas, trimmed],
      }));
      setFocusAreaInput('');
    }
  };

  const handleRemoveFocusArea = (area: string) => {
    setPrompt((prev) => ({
      ...prev,
      focus_areas: prev.focus_areas.filter((a) => a !== area),
    }));
  };

  const handleFocusAreaKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddFocusArea();
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-8">
        <Spinner size="glyph" className="!h-6 !w-6" />
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('Loading customization...')}</p>
      </div>
    );
  }

  return (
    <div className="p-5 space-y-6 bg-white dark:bg-gray-800">

      {/* Info banner */}
      <div data-tour-id="customize-info-banner" className="flex items-start gap-3 p-3 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg">
          <PiInfoLight className="h-5 w-5 text-info-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-info-700 dark:text-info-300">
            {t(`Customize how this document is generated. Your instructions will be applied the next time this ${documentType} is generated or rerun.`)}
          </p>
        </div>

        {/* Section: Custom Instructions */}
        <section data-tour-id="customize-instructions">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            {t('Custom Instructions')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {t('Provide specific instructions for how this document should be structured, what to include or exclude, and any special requirements.')}
          </p>
          <Textarea
            id="custom-instructions"
            label=""
            placeholder={t('e.g. Focus on contractual obligations in section 3. Include a summary table of key dates. Use numbered paragraphs throughout.')}
            value={prompt.instructions}
            rows={5}
            onChange={(e) => setPrompt((prev) => ({ ...prev, instructions: e.target.value }))}
          />
        </section>

        {/* Section: Tone */}
        <section data-tour-id="customize-tone">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            {t('Writing Tone')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {t('Set the overall tone and style of the generated document.')}
          </p>
          <Dropdown
            id="tone-select"
            options={getToneOptions(t)}
            value={prompt.tone}
            onChange={(val) => setPrompt((prev) => ({ ...prev, tone: val as string }))}
            placeholder={t('Select tone...')}
            fullWidth
          />
        </section>

        {/* Section: Structure Template */}
        <section>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            {t('Document Structure Template')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {t('Link an existing structure template to define the document outline. The AI will follow this structure when generating.')}
          </p>

          {!templatesLicensed ? (
            <button
              type="button"
              data-testid="structure-template-upgrade"
              onClick={() => checkSectionGate('templates')}
              className="flex items-center gap-2 w-full px-3 py-2.5 rounded-lg text-start text-sm
                         border border-dashed border-gray-300 dark:border-gray-600
                         text-gray-500 dark:text-gray-400
                         hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <PiLockKeyLight className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1">
                {t('Document templates are not included in your plan.')}
              </span>
              <span className="font-medium text-info-600 dark:text-info-400 flex-shrink-0">
                {t('Upgrade')}
              </span>
            </button>
          ) : loadingTemplates ? (
            <div className="flex items-center gap-2 py-2">
              <Spinner size="glyph" className="!h-4 !w-4" />
              <span className="text-sm text-gray-500">{t('Loading templates...')}</span>
            </div>
          ) : structureTemplates.length > 0 ? (
            <>
              <Dropdown
                id="structure-template-select"
                options={[
                  { value: '', label: t('None (no structure template)') },
                  ...structureTemplates.map((tmpl) => ({
                    value: tmpl.id,
                    label: tmpl.TemplateName,
                  })),
                ]}
                value={prompt.structure_template_id}
                onChange={(val) => setPrompt((prev) => ({ ...prev, structure_template_id: val as string }))}
                placeholder={t('Select a structure template...')}
                fullWidth
              />

              {/* Show selected template info */}
              {prompt.structure_template_id && (() => {
                const selected = structureTemplates.find((t) => t.id === prompt.structure_template_id);
                if (!selected) return null;
                const outline = selected.Structure?.outline || [];
                const sectionCount = outline.length;
                return (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <PiListLight className="h-4 w-4 text-info-500" />
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{selected.TemplateName}</span>
                      </div>
                      <button
                        onClick={() => setPrompt((prev) => ({ ...prev, structure_template_id: '' }))}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                        title={t('Remove template')}
                      >
                        <PiXLight className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {selected.Structure?.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{selected.Structure.description}</p>
                    )}
                    {sectionCount > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
                          {t(`${sectionCount} sections:`, { count: sectionCount })}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {outline.slice(0, 8).map((section: any, idx: number) => (
                            <Badge
                              key={idx}
                              color='blue'
                            >
                              {section.title || section.section}
                            </Badge>
                          ))}
                          {outline.length > 8 && (
                            <span className="text-xs text-gray-400">+{outline.length - 8} more</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic">
              {t('No structure templates available. Create one from the Templates page.')}
            </p>
          )}
        </section>

        {/* Section: Context Sources */}
        <section>
          <div data-tour-id="customize-context-sources">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              {t('Context Sources')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('Choose which matter data to include when generating this document. Disabling a source excludes it from the AI context.')}
            </p>
          </div>
          <div className="space-y-2">
            {CONTEXT_SECTION_CONFIGS
              .filter((section) => section.key !== 'cases' || hasCasesSupport(matterJurisdiction))
              .map((section) => {
              const enabled = prompt.context_sections[section.key] ?? section.defaultEnabled;
              // Check data availability per source:
              // - reference_documents: use locally-fetched attachments list
              // - previous_version: use hasPreviousVersion check
              // - all others: use backend availability map (default to true if key absent)
              let isAvailable: boolean;
              if (section.key === 'reference_documents') {
                isAvailable = attachments.length > 0;
              } else if (section.key === 'previous_version') {
                isAvailable = hasPreviousVersion;
              } else {
                isAvailable = contextAvailability[section.key] !== undefined
                  ? contextAvailability[section.key]
                  : true;
              }
              const isDisabled = !isAvailable;

              const card = (
                <div
                  key={section.key}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                    isDisabled
                      ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700 opacity-40 cursor-not-allowed'
                      : enabled
                        ? 'bg-white dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                        : 'bg-gray-50 dark:bg-gray-800/50 border-gray-100 dark:border-gray-700 opacity-60'
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex-shrink-0 ${isDisabled ? 'text-gray-300 dark:text-gray-600' : enabled ? 'text-primary-500' : 'text-gray-400 dark:text-gray-500'}`}>
                      {section.icon}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium ${isDisabled ? 'text-gray-400 dark:text-gray-500' : enabled ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                        {t(section.label)}
                        {isDisabled && (
                          <span className="ms-2 text-[10px] font-normal text-gray-400 dark:text-gray-500">
                            {t('No data')}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                        {t(section.description)}
                      </p>
                    </div>
                  </div>
                  {isDisabled ? (
                    <Toggle
                      enabled={false}
                      onToggle={() => {}}
                    />
                  ) : (
                    <Toggle
                      enabled={enabled}
                      onToggle={(val) =>
                        setPrompt((prev) => ({
                          ...prev,
                          context_sections: { ...prev.context_sections, [section.key]: val },
                        }))
                      }
                    />
                  )}
                </div>
              );

              return isDisabled ? (
                <Tooltip
                  key={section.key}
                  title={t('No {{source}} data available for this matter. Run analysis or add data to enable this source.', { source: t(section.label).toLowerCase() })}
                  arrow
                  placement="top"
                >
                  <div>{card}</div>
                </Tooltip>
              ) : (
                <React.Fragment key={section.key}>{card}</React.Fragment>
              );
            })}
          </div>
          {Object.values(prompt.context_sections).filter(Boolean).length <= 1 && (
            <div className="flex items-start gap-2 mt-2 p-2 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
              <PiWarningLight className="h-4 w-4 text-warning-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-warning-700 dark:text-warning-300">
                {t('Disabling too many context sources may significantly reduce the quality of the generated document.')}
              </p>
            </div>
          )}
        </section>

        {/* Section: Focus Areas */}
        <section>
          <div data-tour-id="customize-focus-areas">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              {t('Focus Areas')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('Add specific topics or aspects that the AI should pay special attention to.')}
            </p>
          </div>
          <div className="flex items-center gap-2 mb-3">
            <input
              type="text"
              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-info-500 focus:border-transparent outline-none transition-colors"
              placeholder={t('e.g. Liability analysis, damages, key witnesses...')}
              value={focusAreaInput}
              onChange={(e) => setFocusAreaInput(e.target.value)}
              onKeyDown={handleFocusAreaKeyDown}
            />
            <Button
              variant="outline"
              size="small"
              onClick={handleAddFocusArea}
              disabled={!focusAreaInput.trim()}
            >
              {t('Add')}
            </Button>
          </div>
          {prompt.focus_areas.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {prompt.focus_areas.map((area) => (
                <Badge
                  key={area}
                  color="blue"
                  onClose={() => handleRemoveFocusArea(area)}
                >
                  <span className="text-xs">{area}</span>
                </Badge>
              ))}
            </div>
          )}
        </section>

        {/* Section: Additional Context */}
        {/* <section>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            {t('Additional Context')}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            {t('Provide any extra context, background information, or notes that should inform the document generation.')}
          </p>
          <Textarea
            id="additional-context"
            label=""
            placeholder={t('e.g. The client prefers a conservative approach. Previous counsel took an aggressive stance on this issue...')}
            value={prompt.additional_context}
            rows={3}
            onChange={(e) => setPrompt((prev) => ({ ...prev, additional_context: e.target.value }))}
          />
        </section> */}

        {/* Divider */}
        <hr className="border-gray-200 dark:border-gray-700" />

        {/* Section: Reference Attachments */}
        <section>
          <div data-tour-id="customize-attachments">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
              {t('Reference Attachments')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('Upload reference documents that the AI should use when generating this document. Supported: .docx, .txt, .pdf.')}
            </p>
          </div>

          {/* Existing attachments list */}
          {attachments.length > 0 && (
            <ul className="space-y-2 mb-4">
              {attachments.map((attachment) => (
                <li
                  key={attachment.filename}
                  className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 group"
                >
                  <div className="flex items-center gap-2.5 min-w-0 flex-1">
                    <PiFileTextLight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-gray-900 dark:text-white truncate">{attachment.filename}</p>
                      <p className="text-xs text-gray-400">{formatFileSize(attachment.size_bytes)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDeleteAttachment(attachment.filename)}
                    disabled={deletingFile === attachment.filename}
                    className="p-1.5 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 text-gray-400 hover:text-danger-500 transition-colors opacity-0 group-hover:opacity-100"
                    title={t('Remove attachment')}
                  >
                    {deletingFile === attachment.filename ? (
                      <Spinner size="glyph" className="!h-4 !w-4" />
                    ) : (
                      <PiTrashLight className="h-4 w-4" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* Upload area */}
          {uploading ? (
            <div className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
              <Spinner size="glyph" className="!h-4 !w-4" />
              <span className="text-sm text-gray-500">{t('Uploading...')}</span>
            </div>
          ) : (
            <FileUploader
              files={[]}
              onFilesChange={handleUploadFiles}
              allowedExtensions={['.docx', '.txt', '.pdf']}
              maxSizeBytes={10 * 1024 * 1024}
            />
          )}
        </section>

        {/* Status indicator */}
        {hasExistingPrompt && !hasChanges && (
          <div className="flex items-center gap-2 p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg">
            <PiCheckCircleLight className="h-4 w-4 text-success-500 flex-shrink-0" />
            <p className="text-sm text-success-700 dark:text-success-300">
              {t('Custom prompt is active and will be used on the next generation.')}
            </p>
          </div>
        )}

        {hasChanges && (
          <div className="flex items-center gap-2 p-3 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
            <PiWarningLight className="h-4 w-4 text-warning-500 flex-shrink-0" />
            <p className="text-sm text-warning-700 dark:text-warning-300">
              {t('You have unsaved changes.')}
            </p>
          </div>
        )}

      {/* Footer */}
      <div data-tour-id="customize-footer" className="sticky bottom-0 border-t border-gray-200 dark:border-gray-700 px-5 py-4 flex items-center justify-between gap-3 bg-white dark:bg-gray-800">
        <div>
          {hasExistingPrompt && (
            <Button
              variant="destructive"
              size="small"
              onClick={async () => {
                try {
                  await apis.delete(`${basePath}/custom-prompt`);
                  setPrompt({ ...EMPTY_PROMPT });
                  setOriginalPrompt({ ...EMPTY_PROMPT });
                  setHasExistingPrompt(false);
                  dispatch(showFeedbackModal({
                    showModal: true,
                    message: t('Custom prompt removed'),
                    modalType: 'success',
                    duration: 3000,
                  }));
                } catch {
                  dispatch(showFeedbackModal({
                    showModal: true,
                    message: t('Failed to remove custom prompt'),
                    modalType: 'error',
                    duration: 3000,
                  }));
                }
              }}
            >
              {t('Reset All')}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="small" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleSave}
            disabled={saving || !hasChanges}
          >
            {saving ? t('Saving...') : t('Save')}
          </Button>
        </div>
      </div>
      <FeatureTour
        steps={documentCustomizationTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        disableOverlay
      />
    </div>
  );
};

export default DocumentCustomizationPanel;
